import { Component, Fragment, PropsWithChildren } from 'react';
import { withRouter } from '../../../router/withRouter';
import { Link } from 'react-router';
import { Col, Row } from 'react-bootstrap';
import { RouterStore, RootStore } from '../../../store';
import { SpotLoading } from '@enterprise/spot';
import { Form } from '@enterprise/common';
import { inject, observer } from 'mobx-react';
import OrganizationsPageStore from '../OrganizationsPageStore';
import { OrganizationEntity } from '../../../core/entity/OrganizationEntity';
import { FlyOver } from '../../../components/flyover/FlyOver';
import UsersPageStore from '../../users/UsersPageStore';
import { OrganizationalConstants } from '../../../constants';
import { OrganizationEditFormValues, OrganizationFormType } from '../formType/OrganizationFormType';
import arrayMutators from 'final-form-arrays';
import { FeaturesCheckboxes } from '../../../components/formControls/FeaturesCheckboxes';
import { Notifications } from '../../Notifications';
import i18n from '../../../i18n';
import { DeleteWarningModal } from '../../../components/modals/DeleteWarningModal';
import { AppStore } from '../../../store';
import RoleConstants from '../../../constants/RoleConstants';
import { Trans } from 'react-i18next';
import { AccountEntity } from '../../../core/entity/AccountEntity';
import { FormApi } from 'final-form';
import { SiteEntity } from '../../../core/entity/SiteEntity';
import { PricingTierFormControl, OrganizationFormControl, AccountsOrgAndPracticesFormControl } from '../formControls';
import { SystemOwnerFormControl } from '../formControls/systemOwnerFormControl/SystemOwnerFormControl';
import { AnalyticsAccessType } from '../../../core/entity/AnalyticsAccessEntity';
import { AnalyticsFeatureAccessCheckboxes } from '../formControls/AnalyticsFeatureAccessCheckboxes';
import { FEATURES } from '@enterprise/core';
import classNames from 'classnames';

interface OrganizationsEditPageProps {
    page: OrganizationsPageStore;
    usersPage: UsersPageStore;
    routing: RouterStore;
    app: AppStore;
    match: any;
}

interface OrganizationsEditPageState {
    userList?: any;
    loading?: boolean;
    showDeleteModal: boolean;
    updatedOrg?: OrganizationEntity;
}

@inject((allStores: RootStore) => ({
    page: allStores.pages.organizationsPage,
    usersPage: allStores.pages.usersPage,
    routing: allStores.routing,
    app: allStores.app,
}))
@observer
class OrganizationsEditPageComponent extends Component<PropsWithChildren<OrganizationsEditPageProps>, OrganizationsEditPageState> {
    state: OrganizationsEditPageState = {
        userList: undefined,
        showDeleteModal: false,
        updatedOrg: undefined,
    };
    form = new OrganizationFormType();

    CONSTANTS = OrganizationalConstants.default;

    save = async ({ features = [], pricingTierId, analyticsAccessIds = [], ...data }: OrganizationEditFormValues) => {
        try {
            const originalOrg = { ...(this.organization as OrganizationEntity) };
            const updatedOrg = { ...data } as OrganizationEntity;
            const linkAccountIds = updatedOrg.accounts?.filter(({ id }) => !originalOrg.accounts?.find(existing => existing.id === id));
            const unlinkAccountIds = originalOrg.accounts?.filter(({ id }) => !updatedOrg.accounts?.find(existing => existing.id === id));
            const org = await this.store.saveOrg(updatedOrg, linkAccountIds, unlinkAccountIds);
            const accessIds = this.buildAnalyticsAccessIds(pricingTierId, analyticsAccessIds);
            await Promise.all([this.store.saveOrganizationAnalyticsAccess(org.id, accessIds), this.store.saveFeatures(org, features)]);
            this.cancel();
        } catch (err) {
            let message = err;
            if (/\(name\)/g.test(String(message))) {
                message = i18n.t('validation:fieldValueExists', {
                    field: i18n.t('common:orgName'),
                    value: data.name,
                });
            }
            Notifications.fromException(message);
        }
    };

    private buildAnalyticsAccessIds(pricingTierId?: number, featureAccessIds: number[] = []): number[] {
        const ids: number[] = [];
        if (pricingTierId != null) {
            ids.push(pricingTierId);
        }
        ids.push(...featureAccessIds);
        return ids;
    }

    cancel = () => {
        this.props.routing.push(`/${this.CONSTANTS.ROUTES.ORG}`);
    };

    get store(): OrganizationsPageStore {
        return this.props.page;
    }

    get organization(): OrganizationEntity | undefined {
        return this.store.organization;
    }

    get isNewOrganization(): boolean {
        return this.props.match.params.id === 'new';
    }

    get orgId(): string {
        return this.props.match.params.id;
    }

    getUsersForOrg() {
        this.props.usersPage.getUsers().then(users => {
            const userList = users.filter(user => user.organization && user.organization.id === this.orgId);
            this.setState({ userList });
        });
    }

    getUserListElement = (userList) => {
        return userList && userList.length > 0
            ? userList.map((user, index) => {
                  return (
                      <Row key={index}>
                          <Col xs={4}>
                              {user.profile.firstName} {user.profile.lastName}
                          </Col>
                          <Col>{user.username}</Col>
                      </Row>
                  );
              })
            : i18n.t('common:noUsers');
    };

    UNSAFE_componentWillUpdate(nextProps, nextState, nextContext): void {
        if (!this.state.userList) {
            this.getUsersForOrg();
        }
    }

    loadData = async () => {
        this.setState({ loading: true });
        try {
            await Promise.all([this.store.fetchOrganizations(), this.store.fetchAccounts(), this.store.fetchAnalyticsAccess()]);

            if (!this.isNewOrganization) {
                await this.store.fetchOrganization(this.orgId);
            }
        } finally {
            this.setState({ loading: false });
        }
    };

    componentDidMount() {
        if (this.isNewOrganization) {
            this.store.createOrganization();
        }
        this.loadData();
    }

    componentWillUnmount() {
        this.store.dispose();
    }

    render() {
        const { organization, CONSTANTS } = this;
        const { features, analyticsAccessCatalog, organizationAnalyticsAccess: orgAnalyticsAccess } = this.store;
        const { userList } = this.state;
        const flyoverPrefix = this.isNewOrganization ? CONSTANTS.TITLE.NEW : CONSTANTS.TITLE.EDIT;

        const pricingTierId = orgAnalyticsAccess.find(({ type }) => type === AnalyticsAccessType.PricingTier)?.id;
        const analyticsAccessIds = orgAnalyticsAccess.filter(({ type }) => type === AnalyticsAccessType.FeatureAccess).map(({ id }) => id);
        const initData = { ...organization, features, pricingTierId, analyticsAccessIds } as OrganizationEditFormValues;
        const pricingTierOptions = analyticsAccessCatalog.filter(({ type }) => type === AnalyticsAccessType.PricingTier).sort((a, b) => a.id - b.id);
        const analyticsFeatureAccessOptions = analyticsAccessCatalog
            .filter(({ type }) => type === AnalyticsAccessType.FeatureAccess)
            .sort((a, b) => a.name?.localeCompare(b.name));

        const deleteModalProps = {
            title: i18n.t('common:deleteOrgTitle'),
            message: i18n.t('common:deleteOrgWarning'),
            onCancel: () => {
                this.setState({ showDeleteModal: false });
            },
            onDelete: this.delete,
            isVisible: this.state.showDeleteModal,
        };

        return (
            <div>
                {!organization && <SpotLoading />}
                {organization && (
                    <FlyOver
                        title={`${flyoverPrefix} ${CONSTANTS.TITLE.ORG}`}
                        cancelAction={this.cancel}
                        loading={this.state.loading}
                        body={
                            <Form
                                data={initData}
                                formType={this.form}
                                mutators={{
                                    // potentially other mutators could be merged here
                                    ...arrayMutators,
                                }}
                                onSubmit={this.save}
                                keepDirtyOnReinitialize={true}
                            >
                                {({ handleSubmit, form, pristine, invalid }) => {
                                    const formValues = form.getState().values as OrganizationEditFormValues;
                                    const formFeatures = formValues?.features;
                                    const featureListForVisibility = formFeatures != null ? formFeatures : features;
                                    const analyticsAccessDisabled = !featureListForVisibility?.some(f => f.name === FEATURES.ANALYTICS);

                                    return (
                                        <div>
                                            <div>
                                                <div className="fly-over-form">
                                                    <span className={'fly-over-section-title'}>
                                                        <Trans i18nKey="common:detailsFor">Details for</Trans>:
                                                    </span>
                                                    <div className={'fly-over-group'}>
                                                        <OrganizationFormControl />
                                                    </div>

                                                    <span className={'fly-over-section-title'}>
                                                        <Trans i18nKey="common:features">Features</Trans>:
                                                    </span>
                                                    <div className={'fly-over-group'}>
                                                        <Row>
                                                            <Col>
                                                                <FeaturesCheckboxes name="features" />
                                                            </Col>
                                                        </Row>
                                                    </div>

                                                    <span
                                                        className={classNames('fly-over-section-title', {
                                                            'fly-over-section-title--disabled': analyticsAccessDisabled,
                                                        })}
                                                        aria-disabled={analyticsAccessDisabled}
                                                    >
                                                        <Trans i18nKey="common:analyticsAccess">Analytics Access</Trans>:
                                                    </span>
                                                    <div className="fly-over-group" aria-disabled={analyticsAccessDisabled}>
                                                        <Row style={{ marginTop: '16px' }}>
                                                            <Col className={classNames('label-text')}>
                                                                <Trans i18nKey="common:pricingTier">Pricing Tier</Trans>
                                                            </Col>
                                                        </Row>
                                                        <PricingTierFormControl pricingTiers={pricingTierOptions} />
                                                        <Row style={{ marginTop: '16px' }}>
                                                            <Col
                                                                className={classNames('label-text', {
                                                                    'label-text--disabled': analyticsAccessDisabled,
                                                                })}
                                                            >
                                                                <Trans i18nKey="common:accessFeatures">Analytics Access features</Trans>
                                                            </Col>
                                                        </Row>
                                                        <Row>
                                                            <Col>
                                                                <AnalyticsFeatureAccessCheckboxes
                                                                    name="analyticsAccessIds"
                                                                    featureAccessItems={analyticsFeatureAccessOptions}
                                                                    disabled={analyticsAccessDisabled}
                                                                />
                                                            </Col>
                                                        </Row>
                                                    </div>

                                                    {!this.isNewOrganization && (
                                                        <>
                                                            <span className={'fly-over-section-title'}>
                                                                <Trans i18nKey="common:users">Users</Trans>:
                                                            </span>
                                                            <Row>
                                                                <Col xs={12}>
                                                                    <div className={'fly-over-group'}>{this.getUserListElement(userList)}</div>
                                                                </Col>
                                                            </Row>
                                                            {!!userList?.length && (
                                                                <>
                                                                    <span className={'fly-over-section-title'}>
                                                                        <Trans i18nKey="common:systemOwner">System Owner</Trans>:
                                                                    </span>
                                                                    <Row>
                                                                        <Col xs={12}>
                                                                            <div className={'fly-over-group'}>
                                                                                <SystemOwnerFormControl users={userList} />
                                                                            </div>
                                                                        </Col>
                                                                    </Row>
                                                                </>
                                                            )}
                                                        </>
                                                    )}
                                                    <AccountsOrgAndPracticesWrapper form={form} store={this.store} routing={this.props.routing} />
                                                </div>
                                            </div>
                                            <div className="fly-over-actions">
                                                <div className={'spot-modal__footer-cancel-button'} onClick={this.cancel}>
                                                    <Trans i18nKey="common:cancel">cancel</Trans>
                                                </div>
                                                {!this.isNewOrganization && this.userCanDeleteOrg() && (
                                                    <Fragment>
                                                        <button
                                                            style={{ marginRight: '10px' }}
                                                            className="spot-button spot-button--secondary"
                                                            onClick={this.promptDeleteWarning}
                                                        >
                                                            <Trans i18nKey="common:deleteOrgButton">Remove Organization</Trans>
                                                        </button>
                                                        <DeleteWarningModal {...deleteModalProps} />
                                                    </Fragment>
                                                )}
                                                <button
                                                    disabled={pristine || invalid}
                                                    className="spot-button spot-button--primary"
                                                    onClick={handleSubmit}
                                                >
                                                    <Trans i18nKey="common:saveChangesButton">Save Changes</Trans>
                                                </button>
                                            </div>
                                        </div>
                                    );
                                }}
                            </Form>
                        }
                    />
                )}
            </div>
        );
    }

    private promptDeleteWarning = () => {
        this.setState({ showDeleteModal: true });
    };

    private delete = () => {
        const org = this.organization as OrganizationEntity;
        this.store.delete(org.id).then(() => {
            this.setState({ showDeleteModal: false });
            this.props.routing.push(`/${this.CONSTANTS.ROUTES.ORG}`);
        });
    };

    private userCanDeleteOrg = () => {
        return this.props.app.token.hasRole(RoleConstants.SUPER_ADMIN);
    };
}

class AccountsOrgAndPracticesWrapper extends Component<
    PropsWithChildren<{ form: FormApi; store: OrganizationsPageStore; routing: RouterStore }>,
    any
> {
    state = { loading: false };

    render() {
        const { loading } = this.state;
        const { store, form, routing } = this.props;
        const updatedOrg = form.getState().values as OrganizationEntity;
        const otherOrganizations = store.organizations.filter(({ id }) => id !== updatedOrg.id);
        const { accounts, all_sites } = updatedOrg;
        const usedAccountIds = [...otherOrganizations, updatedOrg]
            .reduce<AccountEntity[]>((agg, val) => [...agg, ...(val.accounts || [])], [])
            .map((account) => (typeof account === 'string' ? account : account.id));
        const availableAccounts = store.accounts.filter((account) => !usedAccountIds.includes(account.id));
        return (
            <AccountsOrgAndPracticesFormControl
                accounts={accounts}
                availableAccounts={availableAccounts}
                sites={all_sites}
                organization={store.organization}
                onAddAccount={this.onAddAccount}
                onRemoveAccount={this.onRemoveAccount}
                onUpdatePractice={this.onUpdatePractice}
                loading={loading}
                page={store}
                routing={routing}
            />
        );
    }

    private onAddAccount = (account: AccountEntity) => {
        if (!account) {
            return;
        }

        const { store, form } = this.props;
        const { accounts, all_sites } = form.getState().values as OrganizationEntity;
        form.change('accounts', [...(accounts || []), account]);

        this.setState({ loading: true });
        store
            .fetchAccountApplications(account.id)
            .then(() => {
                const sites = [...all_sites, ...store.accountApplications.filter(({ accountId }) => accountId === account.id)];
                form.change('all_sites', sites);
                this.setState({ loading: false });
            })
            .catch(() => {
                this.setState({ loading: false });
            });
    };

    private onRemoveAccount = (account: AccountEntity) => {
        if (!account) {
            return;
        }

        const { form } = this.props;
        const { all_sites, accounts } = form.getState().values as OrganizationEntity;
        form.change(
            'accounts',
            accounts?.filter(({ id }) => id !== account.id),
        );

        form.change(
            'all_sites',
            all_sites.filter(({ accountId }) => accountId !== account.id),
        );
    };

    private onUpdatePractice = (practice: SiteEntity) => {
        if (!practice) {
            return;
        }

        const { form } = this.props;
        const { all_sites } = form.getState().values as OrganizationEntity;
        const filtered = all_sites.filter(p => {
            return p.id !== practice.id;
        });
        filtered.push(practice);
        form.change('all_sites', filtered);
    };
}

export const OrganizationsEditPage = withRouter(OrganizationsEditPageComponent as any);
